import { ParagraphNode, RangeSelection, TextNode, $create as lexicalCreate } from 'lexical';

import { PrefixNode, PrefixOption } from './prefix-node';
import { ValueNode } from './value-node';
import { collectNodes, getSelectionEdges } from './utils';

export type NodeType = typeof TextNode | typeof PrefixNode | typeof ValueNode;

export interface NodePosition {
  type: NodeType;
  start: number;
  end: number;
}

// TODO: write test cases for this
export const processRaw = (prefixes: PrefixOption[], rawText: string): string => { // This is text that comes from an external source, e.g. paste event
  // strip newlines
  rawText = rawText.replace(/(?<=\S)\r?\n(?=\S)/g, ' ').replace(/\r?\n/g, '');

  const isWord = (ch: string) => /[A-Za-z0-9_]/.test(ch);

  let i = 0;
  let out = '';
  let appendedTrailingNewlineForUnterminated = false;

  while (i < rawText.length) {
    const start = i;

    // 1) Try to read a word prefix
    if (!isWord(rawText[i])) {
      out += rawText[i++];
      continue;
    }

    let j = i;
    while (j < rawText.length && isWord(rawText[j])) j++;
    const prefix = rawText.slice(i, j);

    // Must be followed by ":" to be considered a prefix
    if (rawText[j] !== ':') {
      // Not a prefix: just copy the first char and continue scanning
      out += rawText[i];
      i += 1;
      continue;
    }

    // Check if prefix is allowed (including aliases)
    if (!prefixes.find((o) => o.prefix === prefix || o.aliases?.includes(prefix))) {
      // Not allowed as a prefix; treat as plain text
      out += rawText[i];
      i += 1;
      continue;
    }

    // Emit everything before the prefix as-is
    out += rawText.slice(start, j + 1); // include the colon
    i = j + 1;

    // Skip whitespace
    while (rawText[i] === ' ') i += 1;

    // 2) Parse value
    if (i >= rawText.length) {
      // No value; nothing else to do
      continue;
    }

    if (rawText[i] === '"') {
      // Quoted value — consume a properly CLOSED quoted string
      i++; // skip opening quote
      const valStart = i;
      let value = '';
      let closed = false;

      while (i < rawText.length) {
        const ch = rawText[i];
        if (ch === '\\') {
          // Escape sequence inside quoted value
          if (i + 1 < rawText.length) {
            const next = rawText[i + 1];
            // Only unescape \" and \\ — leave others as-is
            if (next === '"' || next === '\\') {
              value += next;  // \" -> " ; \\ -> \
              i += 2;
              continue;
            } else {
              // Keep unknown escapes literally (e.g., \n -> \n)
              value += ch + next;
              i += 2;
              continue;
            }
          } else {
            // Lone backslash at end
            value += ch;
            i += 1;
            continue;
          }
        } else if (ch === '"') {
          // Closing quote found
          closed = true;
          i += 1; // consume closing quote
          break;
        } else {
          value += ch;
          i += 1;
        }
      }

      if (closed) {
        // We have a complete quoted value → normalize + newline
        out += value + '\n';
      } else {
        // Unterminated quoted value: put back the opening quote and the raw tail
        // (No newline here; we’ll append exactly one at the very end.)
        const rawQuoted = '"' + rawText.slice(valStart);
        out += rawQuoted;
        if (!appendedTrailingNewlineForUnterminated) {
          out += '\n';
          appendedTrailingNewlineForUnterminated = true;
        }
        // We consumed to end
        i = rawText.length;
      }
    } else if (!/\s/.test(rawText[i]) && rawText[i] !== '"') {
      // Unquoted token (runs until whitespace or quote)
      const valStart = i;
      while (
        i < rawText.length
        && !/\s/.test(rawText[i])
        && rawText[i] !== '"'
      ) {
        i++;
      }
      const value = rawText.slice(valStart, i);
      out += value + '\n';
    } else {
      // No value after the colon or starts with whitespace/quote (invalid as complete value)
      // Just continue; nothing to normalize or newline to add
    }
  }

  return out;
};

export const parseText = (prefixes: PrefixOption[], input: string, offset = 0): Array<NodePosition> => {
  // Build regex pattern including both main prefixes and their aliases
  const prefixPatterns = prefixes.flatMap(o => 
    [o.prefix, ...(o.aliases || [])].map(p => p + ':'),
  );
  const match  = new RegExp(`(?<=^|\\s)(${prefixPatterns.join('|')})`, 'gi').exec(input);
  if (!match) return [{ type: TextNode, start: offset, end: offset + input.length }];

  const nodes = [] as Array<NodePosition>;
  if (match.index > 0) {
    nodes.push({ type: TextNode, start: offset, end: offset + match.index });
  }
  const end = match.index + match[1].length;
  nodes.push({ type: PrefixNode, start: offset + match.index, end: offset + end });
  return nodes.concat(
    parseValue(prefixes, input.substring(end), offset + end),
  );
};

export const parseValue = (prefixes: PrefixOption[], input: string, offset = 0): Array<NodePosition> => {
  const lineBreak = input.indexOf('\n');
  if (lineBreak === -1) return [{ type: ValueNode, start: offset, end: offset + input.length }];

  return [{ type: ValueNode as NodeType, start: offset, end: offset + lineBreak }].concat(
    parseText(prefixes, input.substring(lineBreak + 1), offset + lineBreak + 1),
  );
};

export const extractText = (input: string, position: NodePosition) => input.substring(position.start, position.end);

export const $create = (input: string, position: NodePosition) => (
  lexicalCreate(position.type)
    .setTextContent(extractText(input, position))
    .setMode(position.type === PrefixNode ? 'token' : 'normal')
) as InstanceType<NodeType>;

type SerializeFunction = (...args: [firstNode: TextNode] | [selection: RangeSelection]) => string;
export const serialize: SerializeFunction = (arg) => {
  if (arg instanceof ParagraphNode) return '';

  const [selectionStart, selectionEnd] = arg instanceof TextNode ? [null, null] : getSelectionEdges(arg);
  const nodes = arg instanceof TextNode ? Array.from(collectNodes(arg)) : arg.getNodes() as TextNode[];
  return nodes.reduce((acc, node) => {
    let text = node.getTextContent();
    let space = ' ';
    if (node === selectionStart?.getNode()) {
      if (node === selectionEnd!.getNode()) {
        text = text.substring(selectionStart.offset, selectionEnd.offset);
        space = '';
      } else {
        text = text.substring(selectionStart.offset);
      }
    } else if (node === selectionEnd?.getNode()) {
      text.substring(0, selectionStart.offset);
      space = '';
    }

    if (node instanceof PrefixNode) {
      return acc + text;
    }
    if (node instanceof ValueNode) {
      return acc + JSON.stringify(text) + space;
    }
    if (node instanceof TextNode) {
      return acc + text;
    }
  }, '');
};
